'use client'

import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { type VariantProps } from 'class-variance-authority'

import { mtc, TextColors, TypographyStyles } from '../../../utils/style-helpers'
import {
  closeButtonIconStyles,
  closeButtonStyles,
  descriptionStyles,
  footerStyles,
  overlayStyles,
  sheetVariants,
  titleStyles,
} from './styles'
import { Icon } from '../icon'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { Text } from '../text'

const SheetRoot = SheetPrimitive.Root
const SheetTrigger = SheetPrimitive.Trigger
const SheetClose = SheetPrimitive.Close
const SheetPortal = SheetPrimitive.Portal

const SheetOverlay = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>) => (
  <SheetPrimitive.Overlay
    className={mtc(overlayStyles, className)}
    {...props}
  />
)
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

interface SheetContentProps
  extends React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  dismissible?: boolean
}

const SheetContent = ({
  side = 'right',
  className,
  children,
  dismissible = true,
  ...props
}: SheetContentProps) => (
  <SheetPortal>
    <SheetOverlay />
    <SheetPrimitive.Content
      className={mtc(sheetVariants({ side }), className)}
      {...props}
    >
      {dismissible && (
        <SheetPrimitive.Close className={mtc(closeButtonStyles)}>
          <Icon
            className={mtc(closeButtonIconStyles)}
            icon={solid('close')}
            color="text-foreground"
          />

          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
      {children}
    </SheetPrimitive.Content>
  </SheetPortal>
)
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = (
  props: Omit<React.HTMLAttributes<HTMLDivElement>, 'color'>
) => (
  <Text variant={TypographyStyles.H1} color={TextColors.DEFAULT} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={mtc(footerStyles, className)} {...props} />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>) => (
  <SheetPrimitive.Title className={mtc(titleStyles, className)} {...props} />
)
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>) => (
  <SheetPrimitive.Description
    className={mtc(descriptionStyles, className)}
    {...props}
  />
)
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  SheetTrigger,
  SheetPortal,
  SheetClose,
  SheetOverlay,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}

export const Sheet = Object.assign(SheetRoot, {
  Trigger: SheetTrigger,
  Portal: SheetPortal,
  Close: SheetClose,
  Overlay: SheetOverlay,
  Content: SheetContent,
  Header: SheetHeader,
  Footer: SheetFooter,
  Title: SheetTitle,
  Description: SheetDescription,
})
